import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AdoptionWizard } from './AdoptionWizard';
import { adoptionApi } from '../api';
import { animalsApi } from '@/features/animals/api';
import type { AdoptionFormTemplate, AdopterProfile } from '../types';
import { patchTemplate } from '../utils/patchTemplate';

export function AdoptionWizardPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { toast } = useToast();

    const [template, setTemplate] = useState<AdoptionFormTemplate | null>(null);
    const [profile, setProfile] = useState<AdopterProfile | null>(null);
    const [animal, setAnimal] = useState<any | null>(null); // Animal selecionado
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Obtener IDs de la navegación o URL
    const state = location.state as { animalId?: number; shelterId?: number } | null;
    const animalId = state?.animalId || Number(searchParams.get('animalId'));
    const shelterId = state?.shelterId || Number(searchParams.get('shelterId'));

    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                let effectiveShelterId = shelterId;

                // Si hay animalId, obtener el animal para conseguir su shelterId
                if (animalId) {
                    try {
                        const animalData = await animalsApi.getById(animalId);
                        effectiveShelterId = animalData.shelterId || shelterId;
                        setAnimal(animalData); // Guardar datos del animal
                    } catch (err) {
                        console.error('Error loading animal data:', err);
                        // Continuar con shelterId de URL si falla
                    }
                }

                // 1. Cargar template activo
                // Si no hay shelterId, cargará el global
                const templateData = await adoptionApi.getActiveTemplate(effectiveShelterId || undefined);

                // Aplicar parches de frontend
                const patchedTemplate = patchTemplate(templateData);
                setTemplate(patchedTemplate);

                // 2. Cargar perfil existente (si el usuario está logueado)
                try {
                    const profileData = await adoptionApi.getMyProfile();
                    setProfile(profileData);
                } catch (err) {
                    // Si falla cargar perfil (ej: no logueado o 404), no es bloqueante
                    console.log('No existing profile found or user not logged in');
                }

            } catch (err) {
                console.error('Error loading wizard data:', err);
                setError('No se pudo cargar el formulario de adopción. Por favor, intenta nuevamente más tarde.');
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [shelterId, animalId]);

    const handleComplete = async (formData: any) => {
        if (!template) return;

        try {
            setIsSubmitting(true);

            // Enviar respuestas del formulario (esto crea/actualiza el perfil)
            await adoptionApi.submitFormResponse(template.id, {
                shelterId: Number(shelterId) || template.shelterId || animal?.shelterId || 1, // Convertir a número
                animalId: animalId || undefined,
                answers: formData,
            });

            // Mostrar mensaje de éxito
            if (animalId) {
                toast({
                    title: "¡Formulario enviado!",
                    description: "Tu formulario de adopción ha sido registrado exitosamente.",
                    variant: "default", // success
                });
            } else {
                toast({
                    title: "Perfil actualizado",
                    description: "Tu perfil de adoptante ha sido guardado correctamente.",
                });
            }

            // Redirigir al dashboard
            navigate('/adopter/dashboard');

        } catch (err: any) {
            console.error('Error submitting form:', err);
            toast({
                variant: "destructive",
                title: "Error al enviar",
                description: err.response?.data?.message || "Ocurrió un error al procesar tu solicitud.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Cargando formulario...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container max-w-2xl py-12">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
                <div className="mt-6 flex justify-center">
                    <Button onClick={() => navigate(-1)} variant="outline">
                        Volver
                    </Button>
                </div>
            </div>
        );
    }

    if (!template) {
        return (
            <div className="container max-w-2xl py-12 text-center">
                <h2 className="text-2xl font-bold mb-4">Formulario no disponible</h2>
                <p className="text-muted-foreground mb-6">
                    No se encontró un formulario de adopción activo en este momento.
                </p>
                <Button onClick={() => navigate(-1)}>Volver</Button>
            </div>
        );
    }

    return (
        <div className="container max-w-3xl py-8 sm:py-12">
            <div className="mb-8 text-center">
                <h1 className="text-3xl font-bold tracking-tight mb-2">
                    {template.schemaJson.title || template.name || 'Formulario de Adopción'}
                </h1>
                {template.schemaJson.description && (
                    <p className="text-muted-foreground">
                        {template.schemaJson.description}
                    </p>
                )}
            </div>

            <div className="bg-card border rounded-xl p-6 sm:p-8 shadow-sm">
                <AdoptionWizard
                    template={template}
                    existingProfile={profile}
                    selectedAnimal={animal}
                    onComplete={handleComplete}
                    isSubmitting={isSubmitting}
                />
            </div>
        </div>
    );
}
